// ── Move validation and game rules ──

import type {
  BoardState,
  PlayerColor,
  Strand,
  Vertex,
  LacunaRegion,
} from './state';
import { nextNodeId, nextStrandId } from './state';
import { enumerateFaces, findFaceAtPoint, isLacuna, type Face } from './topology';
import {
  isInsideCircle,
  dist,
  segmentsIntersect,
} from '../board/geometry';

/** Check if a vertex has at least one free socket */
export function hasFreeSockets(v: Vertex): boolean {
  return v.free > 0;
}

/** Get all faces that are live (not exterior, not sealed) */
export function getLiveRegions(
  board: BoardState,
  sealedStrandIds: Set<string>,
): Face[] {
  const faces = enumerateFaces(board);
  return faces.filter(f => !f.isExterior);
}

/** Validate that a node can be placed at (x, y) in the given board state */
export function validateNodePlacement(
  x: number,
  y: number,
  board: BoardState,
): { valid: boolean; face: Face | null; reason?: string } {
  // Must be inside the circle
  if (!isInsideCircle(x, y)) {
    return { valid: false, face: null, reason: 'Outside the board' };
  }

  // Must not be too close to an existing vertex
  for (const v of Object.values(board.vertices)) {
    if (dist({ x, y }, v) < 0.15) {
      return { valid: false, face: null, reason: 'Too close to existing vertex' };
    }
  }

  // Find which face the point is in
  const faces = enumerateFaces(board);
  const face = findFaceAtPoint(x, y, faces, board);

  if (!face) {
    return { valid: false, face: null, reason: 'Not in any region' };
  }

  if (face.isExterior) {
    return { valid: false, face: null, reason: 'Outside the playable area' };
  }

  // Sealed lacunae are permanently removed from play
  if (isLacuna(face)) {
    return { valid: false, face: null, reason: 'This region is a sealed lacuna' };
  }

  // Check the face has at least one boundary vertex with a free socket
  const hasFreeSocket = face.vertices.some(vid => {
    const v = board.vertices[vid];
    return v && v.free > 0;
  });

  if (!hasFreeSocket) {
    return { valid: false, face, reason: 'No free sockets in this region' };
  }

  return { valid: true, face };
}

/** Get valid strand targets: boundary vertices of the face with free sockets.
 *  Reachability is determined topologically — any vertex on the face boundary
 *  is reachable from any interior point without crossing a strand. */
export function getValidStrandTargets(
  face: Face,
  board: BoardState,
  alreadyConnected: string[],
): string[] {
  const connected = new Set(alreadyConnected);

  return face.vertices.filter(vid => {
    if (connected.has(vid)) return false;
    const v = board.vertices[vid];
    return v != null && v.free > 0;
  });
}

/**
 * Validate that a strand from the new node to a target vertex is legal.
 *
 * Since the game is purely topological, the only constraints are:
 * - Target is on the boundary of the face the node was placed in
 * - Target has at least one free socket
 * - Target is not already connected this turn (distinct vertices)
 *
 * Crossing checks are unnecessary because:
 * - Existing strands define face boundaries; a new strand within a face
 *   cannot cross them by definition (it stays inside the face).
 * - New strands from the same node are spokes from a single point;
 *   they share only the origin and never cross each other.
 * - The visual routing algorithm handles curve paths around obstacles.
 */
export function validateStrand(
  _nodePos: { x: number; y: number },
  targetId: string,
  face: Face,
  board: BoardState,
  alreadyConnected: string[],
): { valid: boolean; reason?: string } {
  // Target must be on this face's boundary
  if (!face.vertices.includes(targetId)) {
    return { valid: false, reason: 'Target not on region boundary' };
  }

  // Target must have free socket
  const target = board.vertices[targetId];
  if (!target || target.free <= 0) {
    return { valid: false, reason: 'No free socket' };
  }

  // Must not already be connected this turn
  if (alreadyConnected.includes(targetId)) {
    return { valid: false, reason: 'Already connected to this vertex' };
  }

  return { valid: true };
}

/**
 * Get waypoints for a strand — either the stored player-drawn path
 * or a straight line between endpoints.
 */
export function getStrandWaypoints(
  strand: Strand,
  board: BoardState,
): Array<{ x: number; y: number }> {
  if (strand.waypoints && strand.waypoints.length >= 2) {
    return strand.waypoints;
  }
  const from = board.vertices[strand.from];
  const to = board.vertices[strand.to];
  if (!from || !to) return [];
  return [{ x: from.x, y: from.y }, { x: to.x, y: to.y }];
}

/**
 * Validate that a drawn path (polyline) does not cross any existing strand.
 * Returns { valid: true } or { valid: false, reason }.
 */
export function validatePath(
  newPath: Array<{ x: number; y: number }>,
  board: BoardState,
  excludeStrandIds: string[] = [],
): { valid: boolean; reason?: string } {
  if (newPath.length < 2) return { valid: false, reason: 'Path too short' };

  const excludeSet = new Set(excludeStrandIds);

  for (const strand of Object.values(board.strands ?? {})) {
    if (excludeSet.has(strand.id)) continue;

    const existing = getStrandWaypoints(strand, board);
    if (existing.length < 2) continue;

    // Check every segment pair
    for (let i = 0; i < newPath.length - 1; i++) {
      for (let j = 0; j < existing.length - 1; j++) {
        if (segmentsIntersect(
          newPath[i], newPath[i + 1],
          existing[j], existing[j + 1],
        )) {
          return { valid: false, reason: 'Path crosses an existing strand' };
        }
      }
    }
  }

  // Also check self-intersection (non-adjacent segments)
  for (let i = 0; i < newPath.length - 1; i++) {
    for (let j = i + 2; j < newPath.length - 1; j++) {
      // Skip adjacent segments (they share a point)
      if (j === i + 1) continue;
      if (segmentsIntersect(
        newPath[i], newPath[i + 1],
        newPath[j], newPath[j + 1],
      )) {
        return { valid: false, reason: 'Path crosses itself' };
      }
    }
  }

  return { valid: true };
}

/** Check if the game is over: active player cannot make a legal k=1 move */
export function isGameOver(board: BoardState): boolean {
  const faces = enumerateFaces(board);
  for (const face of faces) {
    if (face.isExterior) continue;
    if (isLacuna(face)) continue; // sealed lacunae are not playable

    // Check if there's at least one boundary vertex with a free socket
    const hasFreeSocket = face.vertices.some(vid => {
      const v = board.vertices[vid];
      return v && v.free > 0;
    });

    if (hasFreeSocket) {
      // There's at least one region where a node could be placed and connected
      return false;
    }
  }
  return true;
}

/**
 * Apply a complete move to the board state.
 * Returns the updated board, new lacunae, and updated scores.
 */
export function applyMove(
  board: BoardState,
  nodeX: number,
  nodeY: number,
  targets: string[],
  color: PlayerColor,
  turnNumber: number,
  strandWaypoints?: Array<Array<{ x: number; y: number }> | undefined>,
  existingLacunae?: Record<string, LacunaRegion>,
): {
  newBoard: BoardState;
  newNodeId: string;
  newStrandIds: string[];
  sealedLacunae: Array<{ id: string; color: PlayerColor; boundaryLength: number; boundaryStrands: string[] }>;
} {
  const k = targets.length;
  const nodeId = nextNodeId(board);

  // Deep clone the board
  const newVertices = { ...board.vertices };
  const newStrands = { ...board.strands };

  // Create the new node
  newVertices[nodeId] = {
    id: nodeId,
    type: 'node',
    x: nodeX,
    y: nodeY,
    sockets: k - 1,
    free: k - 1,
    createdTurn: turnNumber,
  };

  // Create strands and update sockets
  const newStrandIds: string[] = [];
  for (let idx = 0; idx < targets.length; idx++) {
    const targetId = targets[idx];
    const sid = nextStrandId({ vertices: newVertices, strands: newStrands });
    const wp = strandWaypoints?.[idx];
    newStrands[sid] = {
      id: sid,
      from: nodeId,
      to: targetId,
      color,
      turn: turnNumber,
      ...(wp && wp.length >= 2 ? { waypoints: wp } : {}),
    };
    newStrandIds.push(sid);

    // Reduce free sockets on target
    const target = { ...newVertices[targetId] };
    target.free = Math.max(0, target.free - 1);
    newVertices[targetId] = target;
  }

  const newBoard: BoardState = { vertices: newVertices, strands: newStrands };

  // Check for new lacunae
  const faces = enumerateFaces(newBoard);
  const sealedLacunae: Array<{
    id: string;
    color: PlayerColor;
    boundaryLength: number;
    boundaryStrands: string[];
  }> = [];

  // Build a set of strand-set keys for already-sealed lacunae so we don't
  // re-detect them. Also tracks keys seen this pass to avoid the dual-face
  // double-count (two faces on opposite sides of the same strands).
  const alreadySealed = new Set<string>();
  for (const lac of Object.values(existingLacunae ?? {})) {
    const key = [...lac.boundaryStrands].sort().join(',');
    alreadySealed.add(key);
  }

  for (const face of faces) {
    if (isLacuna(face)) {
      const strandIds = face.edges
        .filter(e => !e.isArc && e.strandId)
        .map(e => e.strandId!);
      const uniqueStrands = [...new Set(strandIds)].sort();
      const key = uniqueStrands.join(',');

      if (alreadySealed.has(key)) continue; // already sealed in a previous turn
      alreadySealed.add(key); // prevent dual-face double-count this turn

      sealedLacunae.push({
        id: `L${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        color: face.monochromeColor!,
        boundaryLength: uniqueStrands.length,
        boundaryStrands: uniqueStrands,
      });
    }
  }

  return { newBoard, newNodeId: nodeId, newStrandIds, sealedLacunae };
}
